import { useState, useEffect, useContext } from "react";
import { UserContext } from "../../Context";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SearchableDropdown , InputField} from "../../Components/Common";
import { PAYROLL_STATUS, getPayrollStatusColor, ROLES } from "./constants";
import { formatDateDDMMYYYY, toProperCase } from "../../Utils";
import { useESS } from "../ESS/Hooks/useESS.js";
import { usePayroll } from "./Hooks/usePayroll.js";
import { SALARY_MASTER } from "../../routes.js";
import { useParams, useLocation } from "react-router-dom";
import { Loader, } from "../../Components/Common";
import { USER_ROLES } from "../../Constants/Strings.js";

const SalaryMasterAction = () => {
    const navigate = useNavigate();
    const {
        createPayroll,
        loading,
        fetchPayrollById,
        updatePayrollMaster,
    } = usePayroll();
    const { getUsers } = useESS();
    const { user } = useContext(UserContext);
    const { payrollId } = useParams();
    const location = useLocation();

    const isView = location.pathname.includes("view");
    const isAdd = location.pathname.includes("add");
    const [selectedUser, setSelectedUser] = useState(null);

    const [formData, setFormData] = useState({
        user: "",
        start_date: "",
        end_date: "",
        gross_salary: "",
        conveyance_pct: "",
        lta_pct: "",
        esic_eligibility: "",
        pf_eligibility: "YES",
        is_tds_applicable: "NO",
        tds_percentage: "10",
        status: PAYROLL_STATUS.PENDING_FOR_APPROVAL
    });

    useEffect(() => {
        if (isView && payrollId) {
            fetchPayrollById(payrollId).then((data) => {

                setSelectedUser({
                    id: data.user,
                    first_name: data.username
                });

                setFormData({
                    user: data.user,
                    start_date: data.start_date,
                    end_date: data.end_date,
                    gross_salary: data.gross_salary,
                    conveyance_pct: data.conveyance_pct,
                    lta_pct: data.lta_pct,
                    esic_eligibility: data.esic_eligibility ? "YES" : "NO",
                    pf_eligibility: data.pf_eligibility !== false ? "YES" : "NO",
                    is_tds_applicable: data.is_tds_applicable ? "YES" : "NO",
                    tds_percentage: data.tds_percentage ?? "10",
                    status: data.status,
                    userDetails: data.userDetails,
                });
            });
        }
    }, [payrollId]);
    const disableAll = isView;

    const handleChange = (key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
    };

    const handleSubmit = async () => {
        const payload = {
            ...formData,
            pf_eligibility: formData.pf_eligibility === "YES",
            esic_eligibility: formData.esic_eligibility === "YES",
            is_tds_applicable: formData.is_tds_applicable === "YES",
        };
        if (payload.conveyance_pct === "") delete payload.conveyance_pct;
        if (payload.lta_pct === "") delete payload.lta_pct;

        const res = await createPayroll(payload);
        if (res) navigate(SALARY_MASTER);
    };

    const handleEdit = async (action) => {
        const res =
            await updatePayrollMaster(payrollId, { action });
        if (res) navigate(SALARY_MASTER);
    };

    const renderEmployeeDetails = () => {
        if (!isView) return null;
        return (
            <div className="w-full md:w-[520px]">

                <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2 mt-2 ">
                    <span className="inline-block w-1.5 h-6 bg-primary rounded-full"></span>
                    Employee Details
                </h3>
                <div className="grid grid-cols-2 gap-3 mt-4">
                    {[
                        ["First Name", formData?.userDetails?.first_name],
                        ["Last Name", formData?.userDetails?.last_name],
                        ["Email", formData?.userDetails?.email],
                        ["Mobile", formData?.userDetails?.mobile],
                        ["Role", formData?.userDetails?.role === USER_ROLES.HR ? USER_ROLES.HR  : toProperCase(formData?.userDetails?.role)],
                        ["Department", formData?.userDetails?.department],
                        ["Designation", formData?.userDetails?.designation],
                        ["Date Of Joining", formatDateDDMMYYYY(formData?.userDetails?.date_of_joining)],
                    ].map(([label, value]) => (
                        <div key={label}>
                            <p className="text-md font-xl text-primary">{label}</p>
                            <p className="font-semibold text-gray-600 mt-1.5 break-words">{value || "-"}</p>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="p-4 md:p-6">
        
            {/* Header */}
            <div className="flex items-center gap-2 mb-2">
                <button onClick={() => navigate(SALARY_MASTER)}>
                    <ArrowLeft size={23} className="text-primary" />
                </button>
                <h2 className="text-xl font-semibold text-primary">
                    {isAdd ? "Create Salary Master" : "View Salary Master"}
                </h2>
            </div>
            {loading ? (
                <div className="flex justify-center items-center h-full">
                    <Loader />
                </div>
            ) : (
            <>
            <div className="flex flex-col md:flex-row md:gap-8 w-full">
                {renderEmployeeDetails()}
                {!isAdd && (
                    <div className="w-[2px] bg-gray-400 md:mr-8 mt-3 hidden md:block"></div>
                )}
                <div className={`md:mt-3 max-w-[650px] md:mb-2
                ${!isView ? "md:mx-auto" : ""}`}>

                    <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2 mt-4 md:mt-3">
                        <span className="inline-block w-1.5 h-6 bg-primary rounded-full"></span>
                        Salary Details 
                    </h3>

                    <div className={`grid ${isAdd ? "grid-cols-1" : "grid-cols-2"} md:grid-cols-2 gap-4 mt-5`}>
                    {/* Employee Dropdown */}
                    <div className="md:w-[250px]">
                        <label className="font-xl text-md text-primary">Employee<span className="text-red-500">*</span></label>
                            {disableAll ? (
                                <span className="block mt-1">
                                    {formData.user.name || "-"}
                                </span>
                            ) : (
                            <SearchableDropdown
                                fetchFunction={({ search }) => getUsers({ mode: "all", search })}
                                onSelect={(u) => {
                                    setSelectedUser(u);
                                    setFormData((prev) => ({
                                        ...prev,
                                        user: u?.id,
                                        conveyance_pct: u?.company === "TECHB_SOFTWARES" ? "0.10" : "0.25",
                                        lta_pct: u?.company === "TECHB_SOFTWARES" ? "0.15" : "0",
                                    }));
                                }}
                                value={selectedUser}
                                valueKey="id"
                                labelKey="first_name"
                                placeholder="Select Employee"
                                disabled={disableAll}
                            />
                        )}
                    </div>

                    {/* Start Date */}
                    <div className="md:w-[250px]">
                        <label className="block text-md font-xl text-primary">Start Date<span className="text-red-500">*</span></label>
                        <input
                            type="date"
                            disabled={disableAll}
                            className={`w-full border rounded px-3 py-2 mt-1 ${disableAll ? "hidden bg-gray-100 cursor-not-allowed" : ""}`}
                            value={formData.start_date}
                            onChange={(e) => !disableAll && handleChange("start_date", e.target.value)}
                            />
                            <span className={`${disableAll ? "block" : "hidden"}`}>{formatDateDDMMYYYY(formData.start_date)}</span>
                    </div>

                    {/* End Date */}
                    <div className="md:w-[250px]"> 
                        <label className="block text-md font-xl text-primary ">End Date<span className="text-red-500">*</span></label>
                        <input
                            type="date"
                            disabled={disableAll}
                            className={`w-full border rounded px-3 py-2 mt-1 ${disableAll ? "hidden bg-gray-100 cursor-not-allowed" : ""}`}
                            value={formData.end_date}
                            onChange={(e) => !disableAll && handleChange("end_date", e.target.value)}
                            />
                            <span className={`${disableAll ? "block" : "hidden"}`}>{formatDateDDMMYYYY(formData.end_date)}</span>
                    </div>

                    {/* Salary Fields */}
                    <InputField
                        label="Monthly CTC"
                        value={formData.gross_salary}
                        onChange={
                            (v) => handleChange("gross_salary", v)
                        }
                        disabled={disableAll}
                            placeholder="Enter Monthly CTC"
                            ipdata={formData.gross_salary}
                    />
                    <InputField
                        label="Conveyance %"
                        value={formData.conveyance_pct}
                        onChange={(v) => handleChange("conveyance_pct", v)}
                        disabled={disableAll}
                        placeholder="Enter Conveyance %"
                        ipdata={formData.conveyance_pct}
                    />
                    <InputField
                        label="LTA %"
                        value={formData.lta_pct}
                        onChange={(v) => handleChange("lta_pct", v)}
                        disabled={disableAll}
                        placeholder="Enter LTA %"
                        ipdata={formData.lta_pct}
                    />
                        {/* ESIC Eligibility */}
                        <div className="md:w-[250px]">
                            <label className="block text-md font-xl text-primary">
                                ESIC Eligibility<span className="text-red-500">*</span>
                            </label>
                            {disableAll ? (
                                <span className="block mt-1">
                                    {formData.esic_eligibility || "-"}
                                </span>
                            ) : (
                                <select
                                    className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                                    value={formData.esic_eligibility}
                                    onChange={(e) => handleChange("esic_eligibility", e.target.value)}
                                >
                                    <option value="">Select ESIC Eligibility</option>
                                    <option value="YES">YES</option>
                                    <option value="NO">NO</option>
                                </select>
                            )}
                        </div>

                        {/* PF Eligibility */}
                        <div className="md:w-[250px]">
                        <label className="block text-md font-xl text-primary">
                            PF Eligibility<span className="text-red-500">*</span>
                        </label>

                        {disableAll ? (
                            <span className="block mt-1">
                            {formData.pf_eligibility || "-"}
                            </span>
                        ) : (
                            <select
                            className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                            value={formData.pf_eligibility}
                            onChange={(e) => handleChange("pf_eligibility", e.target.value)}
                            >
                            <option value="">Select PF Eligibility</option>
                            <option value="YES">YES</option>
                            <option value="NO">NO</option>
                            </select>
                        )}
                        </div>

                        {/* TDS Applicable */}
                        <div className="md:w-[250px]">
                            <label className="block text-md font-xl text-primary">
                                TDS Applicable<span className="text-red-500">*</span>
                            </label>
                            {disableAll ? (
                                <span className="block mt-1">
                                    {formData.is_tds_applicable || "-"}
                                </span>
                            ) : (
                                <select
                                    className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                                    value={formData.is_tds_applicable}
                                    onChange={(e) => handleChange("is_tds_applicable", e.target.value)}
                                >
                                    <option value="NO">NO</option>
                                    <option value="YES">YES</option>
                                </select>
                            )}
                        </div>

                        {/* TDS % (only meaningful when TDS Applicable = YES) */}
                        {formData.is_tds_applicable === "YES" && (
                            <InputField
                                label="TDS %"
                                value={formData.tds_percentage}
                                onChange={(v) => handleChange("tds_percentage", v)}
                                disabled={disableAll}
                                placeholder="Enter TDS %"
                                ipdata={formData.tds_percentage}
                            />
                        )}

                    {/* Status */}
                    <div className="md:w-[250px]">
                        <label className="block text-md font-xl text-primary">Status</label>
                        <span
                            className={`px-1.5 py-1 rounded-md text-xs font-xl mt-8 ${getPayrollStatusColor(
                                formData.status
                            )}`}
                        >
                            {toProperCase(formData.status)}
                        </span>
                    </div>
                </div>

                {/* Submit Button */}
                {!isView && (
                    <div className="flex justify-end mt-4 mb-3">
                        <button
                            className="px-6 py-2 bg-primary text-white font-semibold rounded hover:bg-green-600"
                            onClick={handleSubmit}
                        >
                            {loading ? "Saving..." : "Save"}
                        </button>
                    </div>
                )}
                    {isView &&
                    user &&
                    [ROLES.DIRECTOR_MANAGEMENT].includes(user.role) &&
                    formData.status === PAYROLL_STATUS.PENDING_FOR_APPROVAL &&
                    (
                        <div className="flex justify-end mt-4 md:mt-6 gap-2">
                            <button
                                className="px-4 py-1 bg-primary text-white font-semibold rounded hover:bg-green-600"
                                onClick={() => handleEdit("approve")}
                            >
                                Approve
                            </button>
                            <button
                                className="px-4 py-1 bg-red-500 text-white font-semibold rounded hover:bg-red-600"
                                onClick={() => handleEdit("reject")}
                            >
                                Reject
                            </button>
                        </div>
                    )}

                </div>
            </div>
            </>
    )
}
        </div>
    );
};

export default SalaryMasterAction;
